import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { di } from '../../../state/inversify.config';
import { AppointmentPrivacyModal } from './appointmentPrivacyModal.dumb';
import { AppointmentPricingModal } from './appointmentPricingModal.dumb';
import { AppointmentDomain } from './appointment.domain';
import { getThirdPartyBookingSystemLink } from './appointment.helpers';
import { Player } from '../../../state/reduxRepositories/players/player.interfaces';
import { Account } from '../../../state/reduxRepositories/accounts/accounts.interfaces';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import { Button } from '../../../components/button/button';
import { Spinner } from '../../../components/spinner/spinner';

export function Appointment() {
  const { t } = useTranslation();
  const { addUserNotification } = useContext(UserNotificationContext);

  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [waitForNewConneqtion, setWaitForNewConneqtion] = useState(false);

  const appointmentDomain = di.get<AppointmentDomain>(AppointmentDomain);
  const selectedPlayerId = appointmentDomain.useSelectPlayerId();
  const user = appointmentDomain.useUserSelector();
  const clinicAccount = appointmentDomain.useClinicAccountSelector();
  const player = appointmentDomain.usePlayerSelector();
  const errors = appointmentDomain.useConneqtErrorsSelector();
  const clinicConneqtsForPlayer =
    appointmentDomain.useClinicConneqtForPlayerSelector();

  const bookAppointment = () => {
    if (!selectedPlayerId) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('PLAYER_SELECTION_REQUIRED').toString(),
        UserNotificationType.ERROR,
      );
      return;
    }
    setShowError(false);
    setShowPricingModal(true);
  };

  const confirmedPricing = () => {
    setShowPricingModal(false);

    if (clinicConneqtsForPlayer?.responsePlayerId) {
      // player already has a conneqt request with the clinic
      loadClinicSite();
    } else {
      setShowPrivacyModal(true);
    }
  };

  const confirmedPrivacy = () => {
    // TODO: This will throw an error in conneqtCreate.thunk if player or clinicAccount is missing. But make sure player and clinic can't be undefined.
    appointmentDomain.dispatchConneqtPlayerWithClinic(
      player as Player,
      clinicAccount as Account,
    );

    setWaitForNewConneqtion(true);
    setIsLoading(true);
    setShowPrivacyModal(false);
  };

  useEffect(() => {
    if (clinicConneqtsForPlayer?.responsePlayerId && waitForNewConneqtion) {
      setWaitForNewConneqtion(false);
      loadClinicSite();
    }
    // This will load the clinic site once the conneqt request has been created. If no conneqt request with the clinic existed for the player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicConneqtsForPlayer]);

  const loadClinicSite = () => {
    setIsLoading(false);
    setShowPrivacyModal(false);

    const url = getThirdPartyBookingSystemLink(
      clinicConneqtsForPlayer?.responsePlayerId as string,
      user,
      player,
    );

    window.open(url, '_blank');
  };

  useEffect(() => {
    if (errors.length) {
      // TODO: retry and tell user if if it keeps failing
      const conneqtExistError = errors.find(
        (error) => error.heading === 'Unable to create conneqt',
      );
      if (conneqtExistError) {
        addUserNotification(
          UserNotificationType.ERROR,
          conneqtExistError.message,
          UserNotificationType.ERROR,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <>
      {isLoading ? (
        <Spinner
          open={isLoading}
          text={t('TELEHEALTH_APPOINTMENT_PLEASE_WAIT').toString()}
        />
      ) : (
        <>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            onClick={bookAppointment}
            sx={{ my: 2 }}
            label={t('BOOK_TELEHEALTH_APPOINTMENT')}
          />
          <AppointmentPricingModal
            onDismiss={() => setShowPricingModal(false)}
            onConfirm={() => confirmedPricing()}
            showPricingModal={showPricingModal}
            setShowPricingModal={setShowPricingModal}
          />
          <AppointmentPrivacyModal
            onDismiss={() => setShowPrivacyModal(false)}
            onConfirm={() => confirmedPrivacy()}
            showPrivacyModal={showPrivacyModal}
            setShowPrivacyModal={setShowPrivacyModal}
          />
        </>
      )}
    </>
  );
}
